import React, { useEffect, useRef, ChangeEvent, FC, useMemo } from "react";
import { OnChangeRange } from "src/@types/form";
import "./range.scss";

const RANGE_RATIO = 0.1;

const DEFAULT_INPUT_RANGE_CLASSNAME =
  "absolute w-full -top-1 h-2 bg-transparent appearance-none pointer-events-none";

interface Props {
  initialMin: number;
  initialMax: number;
  min: number;
  max: number;
  step?: number;
  limitValue?: number;
  onChangeRange: OnChangeRange;
}

export const RangeSlider: FC<Props> = ({
  initialMin,
  initialMax,
  min,
  max,
  step: propStep,
  limitValue = max * RANGE_RATIO,
  onChangeRange,
}) => {
  const progressRef = useRef<HTMLDivElement>(null);

  const step = useMemo(() => {
    if (propStep !== undefined) return propStep; // use given step if available.

    const range = max - min; // calculate the range.
    if (range <= 10) return 0.1; // small step for small range.
    if (range <= 100) return 1; // larger step for medium range.

    // for bigger ranges, calculate step based on range size.
    const magnitude = Math.floor(Math.log10(range));
    return Math.pow(10, magnitude - 2);
  }, [min, max, propStep]);

  const onChangeMinValue = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);

    const isInRange =
      initialMax - value >= limitValue && value >= min && value <= initialMax;

    if (isInRange) {
      onChangeRange(value, initialMax);
    }
  };

  const onChangeMaxValue = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);

    const isInRange =
      value - initialMin >= limitValue && value <= max && value >= initialMin;

    if (isInRange) {
      onChangeRange(initialMin, value);
    }
  };

  useEffect(() => {
    if (progressRef.current) {
      progressRef.current.style.left = `${
        ((initialMin - min) / (max - min)) * 100
      }%`;
      progressRef.current.style.right = `${
        100 - ((initialMax - min) / (max - min)) * 100
      }%`;
    }
  }, [initialMin, initialMax, min, max]);

  return (
    <div className="py-3 overflow-hidden">
      <div className="relative h-0.5 bg-purple-base/50">
        <span
          className="absolute h-0.5 bg-purple-base transition-none"
          ref={progressRef}
        />

        <div className="range">
          <input
            className={DEFAULT_INPUT_RANGE_CLASSNAME}
            type="range"
            onChange={onChangeMinValue}
            value={initialMin}
            min={min}
            max={max}
            step={step}
          />

          <input
            className={DEFAULT_INPUT_RANGE_CLASSNAME}
            type="range"
            onChange={onChangeMaxValue}
            value={initialMax}
            min={min}
            max={max}
            step={step}
          />
        </div>
      </div>
    </div>
  );
};
